/* eslint-disable dot-notation */
import {
  FC, SyntheticEvent, useCallback, useEffect, useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { some } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';

import useDebounce from '../../../hooks/useDebounce';
import { IPaginationModel } from '../../../interfaces/IPaginationModel';

import './AppSelect.scss';

export interface ISearchRequest extends IPaginationModel {
  searchValue?: string
}

const DROPDOWN_HEIGHT = 180;

interface IComponentProps {
  name?: string;
  required?: boolean;
  disabled?: boolean;
  fieldStyle?: any;
  formItemName: string;
  error?: boolean;
  helperText?: string;
  options?: any[] | null;
  isLoading?: boolean;
  getOptions?: (value: ISearchRequest) => void;
  async?: boolean;
  selectedOption?: any;
  onChangeValue?: (value: any) => void;
  getOptionValue?: (option: any) => string;
  getOptionLabel?: (option: any) => string;
  totalCount?: number;
  disabledOptions?: any[];
  trigger?: object;
  className?: string;
  getOptionDisabled?: (value: any) => boolean;
  freeSolo?: boolean;
  dataTestId?: string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  multiline?: boolean;
}

const AppMultiSelectSearch: FC<IComponentProps> = ({
  name,
  required = false,
  disabled = false,
  fieldStyle,
  formItemName,
  options,
  isLoading,
  getOptions,
  onChangeValue,
  async = false,
  getOptionValue,
  totalCount,
  getOptionLabel,
  disabledOptions,
  getOptionDisabled,
  freeSolo = false,
  className = '',
  dataTestId = '',
  selectedOption = [],
  isOptionEqualToValue,
  multiline = false,
}) => {
  const ctx = useFormContext();

  const [open, setOpen] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>({
    pageSize: 5,
    page: 1,
  });
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [value, setValue] = useState<any>(selectedOption || []);

  const debounceSearchValue = useDebounce(searchValue);

  const onChangeSelect = useCallback((_e: any, value: any) => {
    setValue(value);

    if (onChangeValue) {
      onChangeValue?.(value);
    } else {
      ctx.setValue(formItemName, value);
    }
  }, [ctx, onChangeValue]);

  const onChangeSearchInput = (value: string) => {
    setSearchValue(value);
  };

  useEffect(() => {
    setValue((prev) => {
      if (selectedOption?.length !== value?.length) {
        return selectedOption;
      }
      return prev;
    });
  }, [selectedOption]);

  useEffect(() => {
    if (!options) {
      setPaginationModel((prev) => ({ ...prev, page: 1 }));
    }
  }, [options]);

  useEffect(() => {
    if (open) {
      getOptions?.({
        searchValue: debounceSearchValue,
        ...paginationModel,
      });
    }
  }, [debounceSearchValue, paginationModel, open]);

  useEffect(() => {
    const options = searchValue?.split(' ');

    if (freeSolo && (options?.length ?? 0) > 1) {
      const newValue = value
        .concat(options)
        .map((newVal: string) => newVal.trim())
        .filter((newVal: string) => newVal);

      onChangeSelect(undefined, newValue);
      setSearchValue('');
    }
  }, [searchValue, freeSolo]);

  return (
    <Controller
      name={formItemName}
      control={ctx.control}
      render={({ field }) => (
        <FormControlLabel
          className={classNames('app-select', className, { freeSolo })}
          control={(
            <Autocomplete
              open={open}
              loading={!!isLoading}
              onOpen={() => {
                setOpen(!freeSolo);
              }}
              onClose={() => {
                setOpen(false);
              }}
              {...field}
              multiple
              freeSolo={freeSolo}
              value={Array.isArray(value) ? value : []}
              autoSelect={freeSolo}
              onChange={onChangeSelect}
              disabled={disabled}
              defaultValue={[]}
              sx={{ ...fieldStyle }}
              options={options ?? []}
              blurOnSelect={!freeSolo}
              data-testid={dataTestId}
              openOnFocus
              disableClearable={!value}
              getOptionLabel={(option) => getOptionLabel?.(option) || option?.name || ''}
              renderOption={(props, option) => {
                const _value = getOptionValue?.(option) || option?.id;
                const _label = getOptionLabel?.(option) || option?.name || '';
                return (
                  <MenuItem
                    {...props}
                    key={_value}
                    value={_value}
                  >
                    {_label}
                  </MenuItem>
                );
              }}
              inputValue={searchValue || ''}
              onInputChange={(_e, value) => onChangeSearchInput(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={required}
                  className="text-field"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              renderTags={(values, getTagProps) => (
                <Box className={classNames('tags-wrapper', className, { multiline })}>
                  {values?.map((value, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={getOptionLabel?.(value)}
                    />
                  ))}
                </Box>
              )}
              getOptionDisabled={(option) => {
                if (getOptionDisabled) {
                  return getOptionDisabled(option);
                }
                return disabledOptions ? some(disabledOptions, option) : false;
              }}
              isOptionEqualToValue={(option, value) => {
                if (isOptionEqualToValue) {
                  return isOptionEqualToValue(option, value);
                }
                if (freeSolo) {
                  return option === value;
                }
                const optionId = option?.id ?? option?._id;
                const valueId = value?.id ?? value?._id;
                if (valueId) {
                  return valueId === optionId;
                }
                if (optionId) {
                  return value.findIndex(({ id }: any) => id === optionId) !== -1;
                }
                return value.findIndex((item: any) => item === option) !== -1;
              }}
              popupIcon={<FontAwesomeIcon icon={faAngleDown} />}
              slotProps={{
                listbox: {
                  sx: { maxHeight: DROPDOWN_HEIGHT },
                  className: 'app-select-search',
                  onScroll: (event: SyntheticEvent) => {
                    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
                    if (
                      Math.abs(scrollTop + clientHeight - scrollHeight) <= 1
                      && async
                      && totalCount
                      && paginationModel.page * paginationModel.pageSize < totalCount
                    ) {
                      setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }));
                    }
                  },
                },
              }}
            />
          )}
          required={required}
          label={name}
          labelPlacement="top"
        />
      )}
    />
  );
};

export default AppMultiSelectSearch;
